import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Button } from '@tremor/react';
import { toast } from 'react-toastify';
import { Trans } from '@lingui/macro';

import { Wrapper, DataTable, articleColumns, DeleteModal } from '@/components';
import { useTable, useSearch, useQuery } from '@/hooks';
import { getArticlesQueryKey, useDeleteArticle, useArticles } from '@/api';
import type { Article } from '@/api/model';

export const Route = createFileRoute('/_authenticated/articles/')({
  component: Articles,
});

function Articles() {
  const { queryClient } = Route.useRouteContext({
    select: ({ queryClient }) => ({ queryClient }),
  });
  const { pagination, setPagination, sorting, setSorting } = useTable();
  const [searchQuery, setSearchQuery] = useSearch();
  const { queryParameters, queryOptions } = useQuery({
    pagination,
    sorting,
    searchQuery,
    searchFields: ['title'],
  });
  const { data } = useArticles(queryParameters, queryOptions);
  const { articles = [], meta } = data ?? {};

  const { mutateAsync: deleteArticle } = useDeleteArticle({
    mutation: {
      async onSuccess() {
        await queryClient.invalidateQueries({
          queryKey: getArticlesQueryKey(),
        });
      },
    },
  });

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article>();

  const onDelete = async () => {
    if (!selectedArticle) return;

    await deleteArticle({ id: selectedArticle.id });
    setOpenDeleteModal(false);
    toast.success(<Trans>Article deleted successfully!</Trans>);
  };

  return (
    <Wrapper
      header={<Trans>Articles</Trans>}
      actions={
        <Link to='/articles/new'>
          <Button variant='secondary'>
            <Trans>Add new</Trans>
          </Button>
        </Link>
      }
      onSearch={setSearchQuery}
    >
      <DataTable
        data={articles}
        columns={articleColumns({
          onDelete(article) {
            setSelectedArticle(article);
            setOpenDeleteModal(true);
          },
        })}
        pageCount={meta?.pagination?.pages}
        pagination={pagination}
        sorting={sorting}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
      />
      <DeleteModal
        type={<Trans>Article</Trans>}
        show={openDeleteModal}
        showModal={setOpenDeleteModal}
        onDelete={onDelete}
      />
    </Wrapper>
  );
}
